export type PhysicsIssueCategory =
  | 'Physics Conflict'
  | 'Environment Conflict'
  | 'Material Conflict'
  | 'Lighting Conflict'
  | 'Motion Conflict';

export type PhysicsSeverity = 'impossible' | 'unrealistic' | 'coherent';

export type PhysicsIssue = {
  id: string;
  category: PhysicsIssueCategory;
  severity: PhysicsSeverity;
  text: string;
  explanation: string;
  suggestion: string;
  start: number;
  end: number;
};

type PhysicsRule = {
  id: string;
  category: PhysicsIssueCategory;
  severity: Exclude<PhysicsSeverity, 'coherent'>;
  termsA: string[];
  termsB: string[];
  explanation: string;
  suggestion: string;
  allowWithMagic?: boolean;
};

const PHYSICS_RULES: PhysicsRule[] = [
  {
    id: 'fire-underwater',
    category: 'Physics Conflict',
    severity: 'impossible',
    termsA: ['fire', 'flames', 'burning'],
    termsB: ['underwater', 'submerged', 'deep ocean'],
    explanation: 'Fire underwater detected. Standard combustion requires oxygen-rich conditions.',
    suggestion: 'Replace fire with glowing energy, bioluminescence, or magical flame.',
    allowWithMagic: true,
  },
  {
    id: 'sunlight-night',
    category: 'Lighting Conflict',
    severity: 'impossible',
    termsA: ['bright sunlight', 'direct sunlight', 'harsh sunlight'],
    termsB: ['night', 'midnight', 'moonlit night'],
    explanation: 'Strong sunlight and night scene are physically contradictory in one moment.',
    suggestion: 'Use moonlight, artificial light, or change time of day.',
  },
  {
    id: 'shadows-without-light',
    category: 'Lighting Conflict',
    severity: 'unrealistic',
    termsA: ['hard shadows', 'sharp shadows'],
    termsB: ['no light source', 'pitch black', 'total darkness'],
    explanation: 'Defined shadows require a directional light source.',
    suggestion: 'Add key light, moonlight, practicals, or soften/remove shadow language.',
  },
  {
    id: 'air-explosion-underwater',
    category: 'Environment Conflict',
    severity: 'unrealistic',
    termsA: ['explosion', 'fireball blast', 'shockwave in air'],
    termsB: ['underwater', 'submerged', 'ocean floor'],
    explanation: 'Underwater explosions behave differently than air explosions.',
    suggestion: 'Describe a bubble shockwave, pressure wave, and debris plume.',
    allowWithMagic: true,
  },
  {
    id: 'gravity-floating-heavy',
    category: 'Motion Conflict',
    severity: 'unrealistic',
    termsA: ['floating tank', 'floating boulder', 'floating car'],
    termsB: ['normal gravity', 'earth gravity'],
    explanation: 'Heavy object float behavior conflicts with normal gravity without support.',
    suggestion: 'Add anti-gravity, magnetic lift, zero-g, or support mechanism context.',
    allowWithMagic: true,
  },
  {
    id: 'impossible-reflection',
    category: 'Material Conflict',
    severity: 'unrealistic',
    termsA: ['mirror reflection', 'perfect reflection'],
    termsB: ['rough matte wall', 'charcoal fabric', 'fog volume'],
    explanation: 'Perfect mirror reflections require smooth reflective surfaces.',
    suggestion: 'Switch to soft diffuse bounce or use reflective material.',
  },
  {
    id: 'light-direction-inconsistency',
    category: 'Lighting Conflict',
    severity: 'unrealistic',
    termsA: ['light from left'],
    termsB: ['shadow cast left'],
    explanation: 'Shadow direction appears inconsistent with declared light direction.',
    suggestion: 'Align shadow direction opposite to the key light direction.',
  },
];

const MAGIC_CONTEXT = /(magic|magical|arcane|spell|fantasy|sci-fi|energy field|supernatural|mythic)/i;

const findTermIndex = (lowerPrompt: string, terms: string[]): { index: number; term: string } | null => {
  for (const term of terms) {
    const index = lowerPrompt.indexOf(term.toLowerCase());
    if (index >= 0) return { index, term };
  }
  return null;
};

const hasMagicContextNear = (prompt: string, start: number, end: number): boolean => {
  const from = Math.max(0, start - 40);
  const to = Math.min(prompt.length, end + 40);
  return MAGIC_CONTEXT.test(prompt.slice(from, to));
};

export const analyzePromptPhysics = (prompt: string): PhysicsIssue[] => {
  const lower = prompt.toLowerCase();
  const issues: PhysicsIssue[] = [];

  PHYSICS_RULES.forEach((rule) => {
    const a = findTermIndex(lower, rule.termsA);
    const b = findTermIndex(lower, rule.termsB);
    if (!a || !b) return;

    const start = Math.min(a.index, b.index);
    const end = Math.max(a.index + a.term.length, b.index + b.term.length);

    if (rule.allowWithMagic && hasMagicContextNear(prompt, start, end)) {
      return;
    }

    issues.push({
      id: `physics-${rule.id}-${start}-${end}`,
      category: rule.category,
      severity: rule.severity,
      text: prompt.slice(start, end),
      explanation: rule.explanation,
      suggestion: rule.suggestion,
      start,
      end,
    });
  });

  return issues;
};

export const fixPromptPhysics = (prompt: string, issues: PhysicsIssue[]): string => {
  if (!issues.length) return prompt;

  let next = prompt;
  const sorted = [...issues].sort((a, b) => b.start - a.start);

  sorted.forEach((issue) => {
    const replacement = issue.suggestion;
    next = `${next.slice(0, issue.start)}${replacement}${next.slice(issue.end)}`;
  });

  return next
    .replace(/\s{2,}/g, ' ')
    .replace(/,\s*,/g, ', ')
    .replace(/\s+\./g, '.')
    .trim();
};
